import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { offset, useFloating, useHover, useInteractions } from '@floating-ui/react';
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks';
import { Dexie } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { useHotkeys } from 'react-hotkeys-hook';
import { VList } from 'virtua';

import { apiDelete } from '../lib/api_v2';
import db from '../lib/db';
import { fetchGifs, gifPreviewFile, klipyEnabled } from '../lib/klipy';
import { logError } from '../lib/logger';


import { KEYCHAIN, useRegisterWithKeyMaster } from '../contexts/KeyMaster';
import { useOptionalRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import { BREAKPOINT_SM } from '../lib/breakpoints';
import { fetchAsPngBlob } from './portals/ContextMenuPortal';
import DarkTooltipPortal from './shared/DarkTooltipPortal';
import { GlassWrapper } from './shared/GlassWrapper';
import { MenuButton } from './shared/MenuButton';
import SearchField from './shared/SearchField';
import SettingsTabs from './shared/SettingsTabs';

const PICKER_TABS = [
  { id: 'emojis', label: 'Emojis' },
  { id: 'gifs', label: 'GIFs' },
];

const categoryOrder = [
  'Recent',
  'Custom',
  'Smileys & People',
  'Animals & Nature',
  'Food & Drink',
  'Travel & Places',
  'Activities',
  'Objects',
  'Symbols',
  'Flags',
];

// 448px popup − 24px side padding = 424 = 6 slots × 64px + 5 gaps × 8px.
const EMOJIS_PER_ROW = 6;
const RECENT_EMOJI_MAX_DISPLAY = EMOJIS_PER_ROW * 2;

const EmojiButton = ({ emoji, onClick, onContextMenu, sourceLabel = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    middleware: [offset(10)],
  });

  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const handleImageDragStart = useCallback((event) => event.preventDefault(), []);
  const handleContextMenu = useCallback(
    (event) => {
      if (!onContextMenu) return;
      event.preventDefault();
      onContextMenu(emoji, { x: event.clientX, y: event.clientY });
    },
    [emoji, onContextMenu],
  );

  return (
    <>
      <button
        ref={refs.setReference}
        className="relative flex size-16 cursor-pointer items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10"
        onClick={(event) => onClick(emoji, event)}
        onContextMenu={onContextMenu ? handleContextMenu : undefined}
        {...getReferenceProps()}
      >
        <img
          src={emoji.public_url}
          className="relative size-14 select-none"
          alt={emoji.shortcode}
          draggable={false}
          onDragStart={handleImageDragStart}
        />
      </button>

      {isOpen && (
        <DarkTooltipPortal
          variant="bubble"
          setFloating={refs.setFloating}
          floatingStyles={floatingStyles}
          floatingProps={getFloatingProps()}
          zIndexClass="z-(--stack-emoji-picker-tooltip)"
        >
          <span className="flex flex-col items-center text-center">
            <span>:{emoji.shortcode}:</span>
            {sourceLabel && <span className="text-xs opacity-50">{sourceLabel}</span>}
          </span>
        </DarkTooltipPortal>
      )}
    </>
  );
};

const GIF_SEARCH_DEBOUNCE_MS = 300;

const GifGrid = ({ searchTerm, customerId, onGifSelect }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

  // Pagination lives in refs — it drives fetches from scroll events, not renders.
  const pageRef = useRef(1);
  const hasNextRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTerm(searchTerm), GIF_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!klipyEnabled()) return;

    const controller = new AbortController();
    pageRef.current = 1;
    hasNextRef.current = false;
    setStatus('loading');

    fetchGifs({ q: debouncedTerm, page: 1, customerId, signal: controller.signal })
      .then(({ items: nextItems, hasNext }) => {
        hasNextRef.current = hasNext;
        setItems(nextItems);
        setStatus('loaded');
        scrollerRef.current?.scrollTo({ top: 0 });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        logError('Emojibar', 'Could not load GIFs', { error });
        setStatus('error');
      });

    return () => controller.abort();
  }, [debouncedTerm, customerId]);

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !hasNextRef.current || loadingMoreRef.current) return;
    if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 400) return;

    loadingMoreRef.current = true;
    const nextPage = pageRef.current + 1;
    fetchGifs({ q: debouncedTerm, page: nextPage, customerId })
      .then(({ items: nextItems, hasNext }) => {
        pageRef.current = nextPage;
        hasNextRef.current = hasNext;
        setItems((current) => {
          const seen = new Set(current.map((item) => item.id));
          return [...current, ...nextItems.filter((item) => !seen.has(item.id))];
        });
      })
      .catch((error) => logError('Emojibar', 'Could not load more GIFs', { error }))
      .finally(() => {
        loadingMoreRef.current = false;
      });
  }, [debouncedTerm, customerId]);

  // Greedy 2-column masonry: each tile goes to the currently shorter column,
  // measured in aspect-ratio units since both columns render at equal width.
  const columns = useMemo(() => {
    const cols = [
      { items: [], height: 0 },
      { items: [], height: 0 },
    ];
    items.forEach((item) => {
      const file = gifPreviewFile(item);
      if (!file) return;
      const aspectHeight = file.width ? file.height / file.width : 1;
      const target = cols[0].height <= cols[1].height ? cols[0] : cols[1];
      target.items.push({ item, file });
      target.height += aspectHeight;
    });
    return cols;
  }, [items]);

  if (!klipyEnabled()) {
    return (
      <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-gray-400 dark:text-neutral-500">
        Set KLIPY_APP_KEY to enable GIF search.
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-gray-400 dark:text-neutral-500">
        Couldn&rsquo;t load GIFs. Please try again.
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex-1 overflow-hidden px-3 pb-3">
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="aspect-video animate-pulse rounded-lg bg-black/5 dark:bg-white/10" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-8 text-center text-sm text-gray-400 dark:text-neutral-500">
        No GIFs found.
      </div>
    );
  }

  return (
    <div ref={scrollerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-3 pb-3">
      <div className="flex gap-2">
        {columns.map((column, columnIndex) => (
          <div key={columnIndex} className="flex min-w-0 flex-1 flex-col gap-2">
            {column.items.map(({ item, file }) => (
              <button
                key={item.id}
                type="button"
                title={item.title}
                onClick={() => onGifSelect?.(item)}
                className="cursor-pointer overflow-hidden rounded-lg bg-black/5 hover:opacity-85 dark:bg-white/10"
                style={{ aspectRatio: file.width && file.height ? `${file.width} / ${file.height}` : '4 / 3' }}
              >
                <img src={file.url} alt={item.title} loading="lazy" draggable={false} className="size-full object-cover" />
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const Emojibar = ({ onEmojiSelect, onGifSelect, allowMultiSelect = true, allowCustom = true, allowGifs = true }) => {
  const routeWorkspace = useOptionalRouteWorkspace();
  const workspaceId = routeWorkspace?.workspaceId || null;
  const profileId = routeWorkspace?.profileId || null;
  const [activeTab, setActiveTab] = useState('emojis');
  const [searchTerm, setSearchTerm] = useState('');
  const [gifSearchTerm, setGifSearchTerm] = useState('');
  const [recentEmojis, setRecentEmojis] = useLocalStorage(`emojibar:${profileId || 'anon'}:recent`, []);

  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);

  const vlistRef = useRef(null);
  const rootRef = useRef(null);

  // Right-click menu for deleting your own custom emojis. Rendered inside the
  // popover (not portaled) so the hosts' click-away handlers don't tear the
  // whole picker down when the menu is used; coords are popover-local.
  const [emojiMenuState, setEmojiMenuState] = useState(null);

  // Uploader or workspace admin — mirrors the server's destroy authorization.
  const isAdmin = routeWorkspace?.profile?.role === 'admin';
  const canDeleteEmoji = useCallback(
    (emoji) => emoji.kind === 'custom' && (isAdmin || (Boolean(profileId) && String(emoji.profile_id) === String(profileId))),
    [isAdmin, profileId],
  );

  const handleEmojiContextMenu = useCallback(
    (emoji, point) => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Clamp so the in-tree menu can't get clipped by the popover's rounded overflow.
      const menuHeight = canDeleteEmoji(emoji) ? 100 : 56;
      const x = Math.min(point.x - rect.left, rect.width - 172);
      const y = Math.min(point.y - rect.top, rect.height - menuHeight);
      setEmojiMenuState({ emoji, x, y });
    },
    [canDeleteEmoji],
  );

  const handleCloseEmojiMenu = useCallback(() => setEmojiMenuState(null), []);

  const handleCopyEmoji = useCallback(() => {
    const emoji = emojiMenuState?.emoji;
    setEmojiMenuState(null);
    if (!emoji?.public_url) return;

    // Safari only allows clipboard writes inside the user gesture, so the
    // ClipboardItem must be created synchronously with a promise for the bytes.
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      navigator.clipboard?.writeText?.(emoji.public_url);
      return;
    }
    navigator.clipboard.write([new ClipboardItem({ 'image/png': fetchAsPngBlob(emoji.public_url) })]).catch((error) => {
      logError('Emojibar', 'Failed to copy emoji image, falling back to URL', { error });
      navigator.clipboard?.writeText?.(emoji.public_url);
    });
  }, [emojiMenuState]);

  const handleDeleteEmoji = useCallback(async () => {
    const emoji = emojiMenuState?.emoji;
    setEmojiMenuState(null);
    if (!emoji) return;
    if (!window.confirm(`Delete :${emoji.shortcode}:? Everyone in this space loses it.`)) return;

    try {
      const response = await apiDelete(`/emojis/${emoji.id}`);
      const data = await response.json();
      if (!(response.ok && data.ok === true)) {
        logError('Emojibar', 'Could not delete emoji', { errors: data.errors });
        alert('Could not delete emoji, please try again.');
      }
    } catch (error) {
      logError('Emojibar', 'Could not delete emoji', { error });
      alert('Could not delete emoji, please try again.');
    }
  }, [emojiMenuState]);

  const { unlocked: emojiMenuUnlocked } = useRegisterWithKeyMaster(emojiMenuState ? KEYCHAIN.POPOVER__EMOJIBAR_CONTEXT_MENU : null);
  const handleEmojiMenuEscape = useCallback(() => {
    if (emojiMenuUnlocked()) setEmojiMenuState(null);
  }, [emojiMenuUnlocked]);
  useHotkeys('esc', handleEmojiMenuEscape, { enabled: emojiMenuState != null, enableOnFormTags: true, preventDefault: true }, [
    handleEmojiMenuEscape,
  ]);

  // Load all emojis reactively from Dexie and filter out component shortcodes and deactivated emojis
  const emojis = useLiveQuery(
    async () => {
      // get all active emojis: system-wide + custom from every chat the user is in.
      // resolve_only rows are emojis from chats the user *isn't* in, synced so old
      // messages can render them — never offered here.
      const [system, custom] = await Promise.all([
        db.emojis.where('[workspace_id+sort_order]').between(['system', 0], ['system', Dexie.maxKey], true, true).toArray(),
        db.emojis
          .where('workspace_id')
          .notEqual('system')
          .and((e) => e.sort_order >= 0 && !e.resolve_only)
          .toArray(),
      ]);
      let all = allowCustom ? [...system, ...custom] : system;

      // filter out components
      all = all.filter((e) => !e.shortcode.includes('::'));

      // skip duplicate shortcodes among system emojis; customs sharing a
      // shortcode across chats are distinct emojis and all stay visible
      all = all.filter((e, index, self) => e.kind === 'custom' || index === self.findIndex((t) => t.shortcode === e.shortcode));

      return all;
    },
    [allowCustom],
    [],
  );

  // Chat titles so custom emojis from other chats can say where they're from.
  const workspaceTitles = useLiveQuery(async () => new Map((await db.workspaces.toArray()).map((w) => [w.id, w.title])), [], null);

  const filteredEmojis = useMemo(() => {
    // Only include emojis from categories that are in categoryOrder
    const validEmojis = emojis.filter((emoji) => categoryOrder.includes(emoji.category));

    // Bail if no search term
    if (!searchTerm.trim()) return validEmojis;

    // Filter emojis by search term
    const searchLower = searchTerm.toLowerCase();
    return validEmojis.filter((emoji) => emoji.shortcode && emoji.shortcode.toLowerCase().includes(searchLower));
  }, [emojis, searchTerm]);

  // Group emojis by category
  const emojisByCategory = useMemo(() => {
    const grouped = {};

    // Include Recent category if we have any recent emojis, and no search term
    if (recentEmojis.length > 0 && !searchTerm.trim()) {
      const recent = recentEmojis.map((entry) => emojis.find((e) => e.id === entry.id)).filter(Boolean);
      if (recent.length > 0) grouped['Recent'] = recent;
    }

    filteredEmojis.forEach((emoji) => {
      const category = emoji.category;
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(emoji);
    });

    // Sort emojis within each category
    Object.keys(grouped).forEach((category) => {
      if (category === 'Recent') {
        // Recent are presorted so just trim to the max display
        grouped[category] = grouped[category].slice(0, RECENT_EMOJI_MAX_DISPLAY);
      } else if (category === 'Custom') {
        // Custom emojis are sorted desc by sort_order
        grouped[category].sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0));
      } else {
        // All other emojis are sorted asc by sort_order
        grouped[category].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      }
    });

    return grouped;
  }, [filteredEmojis, recentEmojis, emojis, searchTerm]);

  // Filter category order to only include categories that have emojis
  const sortedCategories = useMemo(() => {
    return categoryOrder.filter((category) => emojisByCategory[category] && emojisByCategory[category].length > 0);
  }, [emojisByCategory]);

  // Create rows for dynamic columns approach
  const emojiRows = useMemo(() => {
    const allEmojis = [];
    let totalIndex = 0;
    const hasSearchTerm = searchTerm.trim().length > 0;

    // First, collect all emojis in order, each category preceded by a title row
    sortedCategories.forEach((category) => {
      const categoryEmojis = emojisByCategory[category];
      if (!categoryEmojis || categoryEmojis.length === 0) return;

      // Section title occupies a full synthetic row; skipped during search
      if (!hasSearchTerm) {
        for (let i = 0; i < EMOJIS_PER_ROW; i++) {
          allEmojis.push({
            id: `header-${category}-${i}`,
            category,
            index: totalIndex++,
            isEmpty: true,
            isHeader: i === 0,
          });
        }
      }

      // Add all emojis from this category
      categoryEmojis.forEach((emoji) => {
        allEmojis.push({ ...emoji, category, index: totalIndex++ });
      });

      // Pad the trailing partial row so the next category starts on its own row
      if (!hasSearchTerm) {
        const emojisInCurrentRow = totalIndex % EMOJIS_PER_ROW;
        if (emojisInCurrentRow > 0) {
          const emptySpacesNeeded = EMOJIS_PER_ROW - emojisInCurrentRow;
          for (let i = 0; i < emptySpacesNeeded; i++) {
            allEmojis.push({
              id: `empty-${category}-${i}`,
              category: null,
              index: totalIndex++,
              isEmpty: true,
            });
          }
        }
      }
    });

    // Then group all emojis into rows
    const rows = [];
    for (let i = 0; i < allEmojis.length; i += EMOJIS_PER_ROW) {
      const rowEmojis = allEmojis.slice(i, i + EMOJIS_PER_ROW);
      const rowIndex = Math.floor(i / EMOJIS_PER_ROW);

      rows.push({
        id: `row-${rowIndex}`,
        emojis: rowEmojis,
        rowIndex,
      });
    }

    return rows;
  }, [sortedCategories, emojisByCategory, searchTerm]);

  const handleEmojiClick = useCallback(
    (emoji, event) => {
      setRecentEmojis((current) => {
        // build a new array without mutating the existing state
        const index = current.findIndex((e) => e.id === emoji.id);

        let next;
        if (index !== -1) {
          // increment frequency and sort by last_used for existing entry
          next = current
            .map((e, i) => (i === index ? { ...e, frequency: e.frequency + 1, last_used: Date.now() } : e))
            .sort((a, b) => (b.last_used || 0) - (a.last_used || 0));
        } else {
          // add new entry to the start of the list and trim
          next = [{ id: emoji.id, frequency: 1, last_used: Date.now() }, ...current.slice(0, RECENT_EMOJI_MAX_DISPLAY - 1)];
        }

        return next;
      });

      if (onEmojiSelect) {
        onEmojiSelect(emoji, { keepOpen: allowMultiSelect && event.metaKey });
      }
    },
    [onEmojiSelect, allowMultiSelect, setRecentEmojis],
  );

  return (
    <div ref={rootRef} className="relative flex h-112 w-md flex-col bg-gray-50 dark:bg-neutral-800">
      {allowGifs && (
        <div className="flex flex-shrink-0 px-4 pt-2">
          {/* Tab look: bare labels with a sliding underline, not a filled toggle.
              The underline is an ::after bar so its rounded ends don't distort the box. */}
          <SettingsTabs
            cornerRadius={0}
            tabs={PICKER_TABS}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            className="gap-4"
            indicatorClassName="!bg-transparent after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-black after:content-[''] dark:after:bg-white"
            tabClassName="justify-center !px-0.5 !py-1.5 duration-75 [&_*]:transition-none"
            activeTabClassName="!text-black dark:!text-white"
            inactiveTabClassName="!bg-transparent !text-gray-500 hover:!text-black dark:!text-neutral-400 dark:hover:!text-white"
          />
        </div>
      )}

      {activeTab === 'emojis' || !allowGifs ? (
        <>
          <div className="flex flex-shrink-0 items-center gap-2 px-3 pt-3 pb-3">
            <SearchField autoFocus={!isBreakpointSm} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {/* Adding emojis is desktop-only; the crop/drag flow isn't built for touch.
                self-stretch keeps the pill exactly the search bar's height. */}
            {/* Styled like the search field beside it so the row reads as one family. */}
            {!isBreakpointSm && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('output:internal:add-emoji-modal'))}
                className="flex shrink-0 cursor-pointer items-center self-stretch rounded-full bg-gray-200/70 px-3.5 text-[13px] leading-5 font-medium text-black hover:bg-gray-200 dark:bg-neutral-700/50 dark:text-neutral-200 dark:hover:bg-neutral-700"
              >
                Add Emoji
              </button>
            )}
          </div>

          <div className="relative flex-1">
            <VList ref={vlistRef} style={{ height: '100%' }} className="emoji-scroller pr-3">
              {emojiRows.map((row) =>
                row.emojis[0]?.isHeader ? (
                  <div key={row.id} className="flex h-9 items-end pr-3 pb-1.5 pl-4">
                    <span className="text-xs leading-4 font-medium text-gray-400 dark:text-neutral-500">{row.emojis[0].category}</span>
                  </div>
                ) : (
                  <div key={row.id} className="pl-3">
                    <div className="grid grid-cols-6 gap-2">
                      {row.emojis.map((emoji) =>
                        emoji.isEmpty ? (
                          <div key={emoji.id} className="size-16" />
                        ) : (
                          <EmojiButton
                            key={emoji.id}
                            emoji={emoji}
                            onClick={handleEmojiClick}
                            onContextMenu={handleEmojiContextMenu}
                            sourceLabel={
                              emoji.kind === 'custom' && emoji.workspace_id !== workspaceId
                                ? workspaceTitles?.get(emoji.workspace_id)
                                : null
                            }
                          />
                        ),
                      )}
                    </div>
                  </div>
                ),
              )}
            </VList>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-shrink-0 items-center gap-2 px-3 pt-3 pb-3">
            <SearchField
              autoFocus={!isBreakpointSm}
              value={gifSearchTerm}
              onChange={(e) => setGifSearchTerm(e.target.value)}
              placeholder="Search Klipy"
            />
          </div>

          <GifGrid searchTerm={gifSearchTerm} customerId={profileId} onGifSelect={onGifSelect} />
        </>
      )}

      {emojiMenuState && (
        <>
          {/* In-popover backdrop: catches clicks so the menu closes without tearing down the picker. */}
          <div className="absolute inset-0 z-10" onMouseDown={handleCloseEmojiMenu} onContextMenu={(event) => event.preventDefault()} />
          <GlassWrapper
            role="menu"
            className="absolute z-20 w-40 gap-0 whitespace-nowrap"
            style={{ left: emojiMenuState.x, top: emojiMenuState.y }}
          >
            <MenuButton role="menuitem" onClick={handleCopyEmoji}>
              Copy
            </MenuButton>
            {canDeleteEmoji(emojiMenuState.emoji) && (
              <MenuButton role="menuitem" variant="danger" onClick={handleDeleteEmoji}>
                Delete
              </MenuButton>
            )}
          </GlassWrapper>
        </>
      )}
    </div>
  );
};

export default Emojibar;
